/**
 * Where a country's gaps are covered.
 *
 * This replaces a "complementary partners" ranking that scored every other
 * entity by how much of A's weakness it could cover. On the v1 delivery that
 * ranking was degenerate: with 57% of assessments at zero and four entities
 * above 3.0, "who covers your gaps" collapses into "who is strongest", and
 * ninety of ninety-two countries were offered the same four names. Measured
 * rather than assumed — moving the match from dimensions to categories, and
 * requiring a gap to be a real gap, changed which capabilities were listed but
 * not who was listed.
 *
 * The ranking was not wrong; the question was. Asked the other way round — for
 * each capability this country lacks, who has it — the same data gives ten
 * distinct answers across eleven categories, and Italy, Mexico, the United
 * Kingdom and France appear alongside the obvious four. It is also a statement
 * of fact rather than a recommendation, which is all the assessment can support.
 */

export interface Assessed {
  /** display name */
  n: string;
  /** ISO 3166-1 alpha-3 */
  iso: string;
  /** category values, keyed "Dimension|Category" */
  cat: Record<string, number | null>;
}

export interface Holder {
  name: string;
  iso: string;
  value: number;
}

export interface Gap {
  /** "Dimension|Category" */
  key: string;
  dimension: string;
  category: string;
  /** what this entity holds, which is the reason it is a gap */
  value: number;
  /** entities that hold the capability, strongest first */
  holders: Holder[];
}

export interface GapOptions {
  /** at or below this, a category counts as a gap. Default 1 — the Emerging band */
  gapBelow?: number;
  /** at or above this, an entity counts as holding the capability. Default 2 */
  heldAt?: number;
  /** how many gaps to report. Default 3 */
  limit?: number;
  /** how many holders to name per gap. Default 3 */
  holders?: number;
  /** entities that should never be named as holders, by ISO — the EU, for one */
  exclude?: string[];
}

/**
 * The capabilities this entity most lacks, and who has them.
 *
 * Categories with no assessment are skipped rather than treated as gaps: an
 * uncollected category is not a weakness, and offering to fill it would be
 * inventing a finding.
 */
export function gapsFor(entity: Assessed, all: Assessed[], opts: GapOptions = {}): Gap[] {
  const { gapBelow = 1, heldAt = 2, limit = 3, holders = 3, exclude = [] } = opts;
  const skip = new Set([entity.iso, ...exclude]);

  const assessed = Object.entries(entity.cat)
    .filter((pair): pair is [string, number] => pair[1] != null);

  const weakest = assessed
    .filter(([, v]) => v < gapBelow)
    .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
    .slice(0, limit);

  return weakest.map(([key, value]) => {
    const [dimension, category] = key.split('|');
    return {
      key,
      dimension,
      category,
      value,
      holders: all
        .filter((e) => !skip.has(e.iso) && (e.cat[key] ?? 0) >= heldAt)
        .sort((a, b) => (b.cat[key] ?? 0) - (a.cat[key] ?? 0) || a.n.localeCompare(b.n))
        .slice(0, holders)
        .map((e) => ({ name: e.n, iso: e.iso, value: e.cat[key] as number })),
    };
  });
}

/**
 * Whether naming holders tells a reader anything they could not get from the
 * overall ranking. When the same handful of entities holds everything, the panel
 * should say so instead of implying the match was tailored.
 */
export function holdersAreConcentrated(gaps: Gap[]): boolean {
  const named = gaps.flatMap((g) => g.holders.map((h) => h.iso));
  if (named.length < 3) return false;
  return new Set(named).size <= Math.ceil(named.length / 2);
}
